<template>
  <Modal :value="modalVisible" @input="(v) => $emit('update:modalVisible', v)" title="AI 生图任务管理中心" width="1200px" :mask-closable="false" class-name="ai-task-modal">
    <!-- 手动表格（替代 iView Table，实现行内展开） -->
    <div class="ai-task-table-wrap">
      <table class="ai-task-table" cellspacing="0" cellpadding="0" style="position: relative">
        <thead>
          <tr>
            <th style="width: 150px">任务ID</th>
            <th style="width: 70px">维度</th>
            <th style="width: 160px">SPU/SKU</th>
            <th style="width: 160px">生图类型</th>
            <th style="width: 90px">预计生成数</th>
            <th style="width: 100px">生成成功数</th>
            <th style="width: 100px">生成失败数</th>
            <th style="width: 160px">创建时间</th>
            <th style="width: 100px">当前状态</th>
            <th style="width: 130px; text-align: center">任务详情</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(task, index) in taskList">
            <!-- 数据行 -->
            <tr class="ai-task-row">
              <td>{{ task.taskNo }}</td>
              <td>{{ taskDimension[task.dimension] || "" }}</td>
              <td>{{ task.productCode }}</td>
              <td>
                <Tag :color="task.generateMode == 2 ? 'orange': 'blue'">{{ task.generateMode == 2 ? "多图合成" : "一对一生成" }}</Tag>
              </td>
              <td>{{ task.expectedGenerationQuantity || 0 }} 张</td>
              <td>
                <span class="ai-task-green">{{ task.successQuantity || 0 }} 张</span>
              </td>
              <td>
                <span style="color: red">{{ task.failQuantity || 0 }} 张</span>
              </td>
              <td>{{ task.createdTime }}</td>
              <td>
                <span class="ai-task-status" :class="statusLabel(task.status).class">
                  {{ statusLabel(task.status).label }}
                </span>
              </td>
              <td style="text-align: center">
                <Button type="primary" size="small" @click="toggleExpand(index, task, $event)">{{ expandIndex === index ? "收起任务详情" : "查看任务详情" }}</Button>
              </td>
            </tr>
            <!-- 展开行（紧跟在数据行下方，带滑动动画） -->
            <tr class="ai-task-expand-row">
              <td colspan="10" style="padding: 0; border: none">
                <div :class="['ai-task-expand-content', expandIndex === index ? 'is-expanded' : '']" :ref="`expandPanel${index}`">
                  <div class="ai-task-expand-inner" v-show="expandIndex === index">
                    <!-- 任务详情面板 -->
                    <div class="ai-task-detail-header">
                      <div class="ai-task-detail-title">
                        <Icon type="ios-search" />
                        <span>任务详细参数与依赖追溯 [{{ task.taskNo }}] - {{ taskDimension[task.dimension] || "" }}维度</span>
                      </div>
                      <!-- 下载/复制下拉菜单 -->
                      <Dropdown v-if="task.lineages && task.lineages.length > 0" style="margin-left: auto" @on-click="(name) => handleBatchAction(name, task)">
                        <Button size="small" icon="ios-download-outline">
                          下载
                          <Icon type="ios-arrow-down"></Icon>
                        </Button>
                        <DropdownMenu slot="list">
                          <DropdownItem name="download">下载图片文件</DropdownItem>
                          <DropdownItem name="copy">复制图片URL</DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>

                    <!-- 参数摘要 -->
                    <div class="ai-task-detail-params">
                      <div class="ai-task-param-item ai-task-param-full">
                        <div class="ai-task-param-label">当时填写的提示词 (Prompt)</div>
                        <div class="ai-task-param-value ai-task-prompt-text">
                          {{ task.prompt }}
                        </div>
                      </div>
                      <div class="ai-task-param-item">
                        <div class="ai-task-param-label">使用的参考图数</div>
                        <div class="ai-task-param-value">{{ getAllReferenceImages(task).length }} 张参考图</div>
                      </div>
                      <div class="ai-task-param-item">
                        <div class="ai-task-param-label">目标存储配置</div>
                        <div class="ai-task-param-value">
                          <span v-if="task.saveLanguage">{{ langOptions[task.saveLanguage].label }}</span>
                          <span v-if="task.imageSetId">{{ getImageSetName(task) }}</span>
                        </div>
                      </div>
                      <div class="ai-task-param-item" v-if="task.dimension == 2">
                        <div class="ai-task-param-label">SKU同步范围</div>
                        <div class="ai-task-param-value">
                          <span v-for="tag in task.syncTo" :key="tag" class="ai-task-sync-tag">{{ syncRange[tag] }}</span>
                          <span v-if="!task.syncTo || task.syncTo.length === 0" style="color: #999">无同步</span>
                        </div>
                      </div>
                      <div class="ai-task-param-item" v-if="task.size">
                        <div class="ai-task-param-label">生成图片尺寸</div>
                        <div class="ai-task-param-value">{{ task.size }}</div>
                      </div>
                      <div class="ai-task-param-item" v-if="task.resolution">
                        <div class="ai-task-param-label">图片分辨率</div>
                        <div class="ai-task-param-value">
                          {{ task.resolution }}
                        </div>
                      </div>
                    </div>

                    <!-- 血缘关系依赖图：每个 lineage 是一行“参考图+提示词→生成图” -->
                    <div class="ai-task-lineage-header">
                      <Icon type="ios-warning" color="#f90" />
                      <span>AI 生产血缘关系依赖图 (哪张参考图 + 哪个提示词 → 生成了哪张图)</span>
                    </div>
                    <div class="ai-task-lineage-list">
                      <div v-for="(lineage, lIdx) in task.lineages" :key="`lineage-${lIdx}`" class="ai-task-lineage-record">
                        <div v-if="task.generateMode == 2" class="ai-task-lineage-record-header">
                          <span class="ai-task-lineage-mode-tag">多图合并</span>
                          <span>生图记录 #{{ lIdx + 1 }} · {{ lineage.referenceImage.length }} 张输入图</span>
                        </div>
                        <div class="ai-task-lineage-box">
                          <!-- 参考图 -->
                          <div class="ai-task-lineage-images">
                            <div v-for="(referenceImage, refIdx) in lineage.referenceImage" :key="`reference-${refIdx}`" class="ai-task-lineage-img-item" @click="previewImage(getAllReferenceImages(task), getReferenceIndex(task, lIdx, refIdx))">
                              <img :src="referenceImage" />
                              <div class="ai-task-lineage-img-label">{{ task.generateMode == 2 ? `图${refIdx + 1}` : "原参考图片" }}</div>
                            </div>
                            <div v-if="lineage.referenceImage.length === 0" class="ai-task-lineage-img-item ai-task-lineage-img-placeholder">
                              <div class="ai-task-lineage-img-label">原图路径丢失</div>
                            </div>
                          </div>
                          <!-- + 号 + 提示词 -->
                          <div class="ai-task-lineage-prompt-section">
                            <span class="ai-task-lineage-plus">+</span>
                            <div class="ai-task-lineage-prompt-content">
                              <div class="ai-task-lineage-prompt-title">当时所填提示词 (Prompt):</div>
                              <div class="ai-task-lineage-prompt-text">"{{ lineage.prompt }}"</div>
                              <!-- 失败原因（仅失败状态展示） -->
                              <div v-if="lineage.status == 3 && lineage.errorMsg" class="ai-task-lineage-error-row">
                                <span class="ai-task-error-msg">失败原因：{{ lineage.errorMsg }}</span>
                              </div>
                            </div>
                          </div>
                          <!-- → 号 + 生成图 -->
                          <div class="ai-task-lineage-arrow">→</div>
                          <!-- 生成图 -->
                          <div class="ai-task-lineage-images">
                            <!-- 失败/删除状态 -->
                            <div v-if="[3, 4].includes(lineage.status)" class="ai-task-lineage-img-item ai-task-lineage-img-failed">
                              <div class="ai-task-failed-text">{{ lineage.status == 3 ? "失败" : "已删除" }}</div>
                              <a
                                class="ai-task-failed-retry-btn"
                                :class="{
                                  'is-loading': retryLoadingMap[lineage.aiGenerateImageTaskDetailId],
                                }"
                                @click="retryTask(lineage)"
                              >
                                <Icon v-if="retryLoadingMap[lineage.aiGenerateImageTaskDetailId]" type="ios-loading" class="ai-task-retry-spin" />
                                <span>{{ retryLoadingMap[lineage.aiGenerateImageTaskDetailId] ? "重试中…" : "重新生成" }}</span>
                              </a>
                            </div>
                            <!-- 已完成状态 -->
                            <div v-else-if="lineage.status == 2">
                              <div v-if="lineage.generatedImage" class="ai-task-lineage-img-item is-generated" @click="previewImage(getAllGeneratedImages(task), lIdx)">
                                <img :src="lineage.generatedImage" />
                                <div class="ai-task-lineage-img-label ai-task-green mb10">生成图成果 #{{ lIdx + 1 }}</div>
                                <div class="ai-task-img-action-btns">
                                  <a class="ai-task-action-link" @click.stop="openRegenerateModal(task, lineage)">重新生成</a>
                                  <template v-if="permission.aiGenerateImageTaskDel">
                                    <span class="ai-task-action-divider">|</span>
                                    <a
                                      class="ai-task-action-link ai-task-action-danger"
                                      :class="{
                                        'is-loading': deleteLoadingMap[lineage.aiGenerateImageTaskDetailId],
                                      }"
                                      @click.stop="deleteAiImage(task, lineage)"
                                      >删除</a
                                    >
                                  </template>
                                </div>
                              </div>
                              <div v-else class="ai-task-lineage-img-item ai-task-lineage-img-placeholder">
                                <div class="ai-task-lineage-img-label">AI图路径为空</div>
                              </div>
                            </div>
                            <!-- 进行中状态 -->
                            <div v-else class="ai-task-lineage-img-item ai-task-lineage-img-placeholder">
                              <Icon type="ios-loading" class="ai-task-placeholder-spin" />
                              <div class="ai-task-lineage-img-label">AI生成中</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <!-- 空态 -->
          <tr v-if="taskList.length === 0 && !loading">
            <td colspan="9" style="text-align: center; padding: 30px 0; color: #999">暂无任务数据</td>
          </tr>
        </tbody>
        <!-- loading 遮罩 -->
        <Spin size="large" fix v-if="loading"></Spin>
      </table>
    </div>

    <!-- 底部按钮 loadTaskList-->
    <div slot="footer">
      <Button @click="loadTaskList"> <Icon type="md-refresh" /> 刷新任务列表 </Button>
      <Button type="primary" @click="$emit('update:modalVisible', false)">关闭看板</Button>
    </div>

    <!-- 重新生成弹框 -->
    <ai-regenerate-modal :visible="regenerateModalVisible" :task="regenerateTask" :lineage="regenerateLineage" @update:visible="regenerateModalVisible = $event" @success="onRegenerateSuccess" />
  </Modal>
</template>

<script>
import { api as viewerApi } from "v-viewer";
import api from "@/api/api";
import aiRegenerateModal from "./aiRegenerateModal.vue";

/**
 * @description AI 生图任务管理中心弹窗
 * @author 前端开发
 * @date 2026-07-21
 * @功能说明
 *   1. 手动表格渲染任务列表（支持行内展开）
 *   2. 手风琴交互：同一时间只展开一行，展开面板紧跟在被点击行下方
 *   3. 展开/收起带 CSS 滑动动画（max-height transition）
 *   4. 展开面板展示：任务参数摘要 + AI 生产血缘关系依赖图
 *   5. 点击图片调用 v-viewer 放大预览（参考图/生成图分池切换）
 */
export default {
  name: "aiGenerateTaskModal",
  components: {
    aiRegenerateModal,
  },
  props: {
    modalVisible: {
      type: Boolean,
      default: false,
    },
    permission: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      downPicLoading: false,
      /** 当前展开行的 index（null 表示全部收起） */
      expandIndex: null,
      /** 任务列表 */
      taskList: [],
      /** 任务维度 1-SPU 2-SKU */
      taskDimension: { 1: "SPU", 2: "SKU" },
      /** 图套类型 */
      taskTypeMap: { 1: "网图", 2: "非实拍自修", 3: "实拍自修" },
      /** 存储语言选项（与语言 tab 保持一致） */
      langOptions: {
        common: { value: "common", label: "通用" },
        en: { value: "en", label: "英语 (EN)" },
        fr: { value: "fr", label: "法语 (FR)" },
        de: { value: "de", label: "德语 (DE)" },
        ru: { value: "ru", label: "俄语 (RU)" },
        nl: { value: "nl", label: "荷兰语 (NL)" },
      },
      // 同步范围
      syncRange: { 1: "同SIZE SKU", 2: "同COLOR SKU" },
      // 根据sku所有语言的 SKU 图套列表
      skuAllLangSkuImageSetList: {},
      currentTaskImageSetList: {},
      // 重新生成按钮 loading 状态：key=aiGenerateImageTaskDetailId(子任务id), value=是否请求中
      retryLoadingMap: {},
      // 删除按钮 loading 状态：key=aiGenerateImageTaskDetailId, value=是否请求中
      deleteLoadingMap: {},
      // 重新生成弹框
      regenerateModalVisible: false,
      regenerateTask: null,
      regenerateLineage: null,
    };
  },
  watch: {
    modalVisible(val) {
      if (val) {
        this.expandIndex = null;
        this.loadTaskList();
      }
    },
    skuAllLangSkuImageSetList: {
      handler(val) {
        if (this.expandIndex === null) return false;
        const task = this.taskList[this.expandIndex];
        let currentImageSet = this.skuAllLangSkuImageSetList[task.correlationId] || [];
        this.currentTaskImageSetList = currentImageSet;
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {},
  methods: {
    // 返回图套名称
    getImageSetName(task) {
      let currentImageSet = this.currentTaskImageSetList[task.imageSetId];
      let name = "";
      if (currentImageSet) {
        let setName = this.taskTypeMap[currentImageSet.setType];
        name = ` - ${currentImageSet.setName} ${setName ? `(${setName})` : ""}`;
      }
      return name;
    },
    /** 状态文本映射 */
    statusLabel(status) {
      // 状态 0-已创建 1-进行中 2-已完成 3-生成失败 4-删除
      const map = {
        0: { label: "已创建", class: "is-created" },
        1: { label: "进行中", class: "is-running" },
        2: { label: "已完成", class: "is-done" },
        3: { label: "生成失败", class: "is-failed" },
      };
      return map[status] || { label: "未知状态", class: "" };
    },
    /** 切换展开/收起（手风琴：同一时间只展开一行） */
    toggleExpand(index, task, event) {
      // this.expandIndex = this.expandIndex === index ? null : index;
      this.loadImageSetList(task);
      const isSame = this.expandIndex === index;
      const willExpand = !isSame; // 是否即将展开（而不是收起）
      this.expandIndex = willExpand ? index : null;
      // 仅在展开时执行滚动
      if (willExpand) {
        // 等待 DOM 更新和展开动画（max-height 过渡）
        this.$nextTick(() => {
          // 获取当前点击的按钮元素
          const btn = event.currentTarget;
          // 向上查找数据行（.ai-task-row）
          const row = btn.closest("tr.ai-task-row");
          if (row) {
            // 平滑滚动到该行，并居中显示
            row.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        });
      }
    },
    /**
     * 图片预览（调用 v-viewer）
     * @param {string[]} images 图片路径数组
     * @param {number} initialIndex 初始展示的索引
     */
    previewImage(images, initialIndex) {
      viewerApi({
        options: {
          toolbar: true,
          url: "dataSource",
          initialViewIndex: initialIndex,
          zIndex: 99999,
        },
        images: (images || []).map((src) => ({ dataSource: src })),
      });
    },
    /** 取任务内所有 lineage 的参考图池（不去重，保持与 lineage 顺序一致） */
    getAllReferenceImages(task) {
      return (task.lineages || []).reduce((images, lineage) => images.concat(lineage.referenceImage || []), []);
    },
    /** 取任务内所有 lineage 的生成图池 */
    getAllGeneratedImages(task) {
      return (task.lineages || [])
        .filter((l) => l.status == 2)
        .map((l) => l.generatedImage)
        .filter((img) => img);
    },
    /** 前面记录的参考图总数加上当前记录内的图片索引 */
    getReferenceIndex(task, lIdx, refIdx) {
      return (task.lineages || []).slice(0, lIdx).reduce((count, lineage) => count + (lineage.referenceImage || []).length, 0) + refIdx;
    },
    getReferenceImages(detail, generateMode, filenodeViewTargetUrl) {
      const mainImages = detail.referenceImagePath ? [filenodeViewTargetUrl + detail.referenceImagePath] : [];
      if (generateMode != 2) {
        return mainImages;
      }
      try {
        const images = JSON.parse(detail.referenceImageJson || "[]");
        if (!Array.isArray(images)) return mainImages;
        const referenceImages = images
          .filter((image) => image && image.imagePath)
          .sort((a, b) => Number(a.sortNo) - Number(b.sortNo))
          .map((image) => filenodeViewTargetUrl + image.imagePath);
        return mainImages.concat(referenceImages);
      } catch (error) {
        return mainImages;
      }
    },
    /**
     * 加载任务列表
     */
    loadTaskList() {
      this.loading = true;
      this.axios
        .post(api.aiGenerateImageTaskList)
        .then((res) => {
          if (res && res.data && res.data.code == 0) {
            const filenodeViewTargetUrl = this.$store.state.erpConfig.filenodeViewTargetUrl;
            this.taskList = (res.data.datas || []).map((task) => {
              return {
                ...task,
                lineages: (task.aiGenerateImageTaskDetailVOList || [])
                  .map((detail) => {
                    return {
                      ...detail,
                      prompt: detail.prompt || task.prompt,
                      referenceImage: this.getReferenceImages(detail, task.generateMode, filenodeViewTargetUrl),
                      generatedImage: detail.generateImagePath ? filenodeViewTargetUrl + detail.generateImagePath : "",
                    };
                  })
                  .filter((k) => {
                    return k.generateType === 1;
                  }),
              };
            });
          } else {
            this.$Message.error(res.data.message);
          }
        })
        .catch((err) => {
          this.$Message.error(typeof err === "string" ? err : (err && err.message) || "加载任务列表失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** 加载SKU图套列表 */
    loadImageSetList(task) {
      const { correlationId, saveLanguage, dimension } = task;
      if (!(correlationId && saveLanguage && dimension == 2)) return;
      if (this.skuAllLangSkuImageSetList[correlationId]) return;
      // 接口返回该sku下的所有图套列表
      this.axios.get(api.get_productGoodsImageSetList + correlationId).then((res) => {
        if (res && res.data && res.data.code === 0) {
          let allImageSetList = {};
          (res.data.datas || []).forEach((k) => {
            allImageSetList[k.productGoodsImageSetId] = k;
          });
          if (allImageSetList && Object.keys(allImageSetList).length > 0) {
            this.$set(this.skuAllLangSkuImageSetList, correlationId, allImageSetList);
          }
        }
      });
    },
    /**
     * 重试失败的AI生图子任务
     * @param {Object} lineage 包含 aiGenerateImageTaskId(主任务id) aiGenerateImageTaskDetailId(子任务id)
     */
    retryTask(lineage) {
      const { aiGenerateImageTaskId, aiGenerateImageTaskDetailId } = lineage || {};
      if (!aiGenerateImageTaskId || !aiGenerateImageTaskDetailId) {
        this.$Message.error("重试失败：缺少任务ID");
        return;
      }
      // 防止多次点击：若该子任务已在重试中，直接跳过
      if (this.retryLoadingMap[aiGenerateImageTaskDetailId]) {
        return;
      }
      // 二次确认
      this.$Modal.confirm({
        title: "重新生成确认",
        content: `<div>确认要对该图片重新生成吗？</div>
                  <div style="margin-top:6px;color:#f60;font-size:12px;">提示：重新生成将重新调用AI接口，可能需要一定时间。</div>`,
        okText: "确认重新生成",
        cancelText: "取消",
        onOk: () => {
          // 标记该子任务为重试中
          this.$set(this.retryLoadingMap, aiGenerateImageTaskDetailId, true);
          return new Promise((resolve) => {
            this.axios
              .post(api.aiGenerateImageTaskRetry, {
                aiGenerateImageTaskId,
                aiGenerateImageTaskDetailId,
                prompt: lineage.prompt,
              })
              .then((res) => {
                if (res && res.data && res.data.code == 0) {
                  this.$Message.success("重试任务已提交");
                  this.loadTaskList();
                } else {
                  this.$Message.error((res && res.data && res.data.message) || "重试失败");
                }
              })
              .catch((err) => {
                this.$Message.error((err && err.message) || "重试失败");
              })
              .finally(() => {
                this.$set(this.retryLoadingMap, aiGenerateImageTaskDetailId, false);
                resolve();
              });
          });
        },
        onCancel: () => {},
      });
    },
    /**
     * Dropdown 批量操作路由
     * @param {string} name DropdownItem 的 name 值
     * @param {Object} task 当前展开的任务对象
     */
    handleBatchAction(name, task) {
      if (name === "download") {
        this.downloadAllImages(task);
      } else if (name === "copy") {
        this.copyAllImageUrls(task);
      }
    },
    /**
     * 复制当前任务所有已生成图片的完整 URL（按顺序）
     * 参考 skuImageSetModal.vue 的 copyCurrentSetImageUrls
     * @param {Object} task 当前展开的任务对象
     */
    copyAllImageUrls(task) {
      const filenodeViewTargetUrl = this.$store.state.erpConfig.filenodeViewTargetUrl;
      const urls = (task.lineages || []).filter((l) => l.status == 2 && l.generateImagePath).map((l) => `${location.origin}/product-service/${filenodeViewTargetUrl}${l.generateImagePath}`);
      if (!urls || urls.length === 0) {
        this.$Message.warning("当前任务没有可复制的图片URL");
        return;
      }
      this.$common
        .copyToClip(urls.join("\n"))
        .then((ok) => {
          this.$Message.success(ok ? "已复制 " + urls.length + " 张图片URL" : "复制失败");
        })
        .catch(() => {
          this.$Message.error("复制失败");
        });
    },
    /**
     * 下载当前任务所有已生成图片打包 zip
     * @param {Object} task 当前展开的任务对象
     */
    downloadAllImages(task) {
      if (this.downPicLoading) {
        this.$Message.warning("下载中，请稍后");
        return;
      }
      const { aiGenerateImageTaskId } = task || {};
      if (!aiGenerateImageTaskId) {
        this.$Message.error("下载失败：缺少任务ID");
        return;
      }
      // 收集所有已生成的子任务ID
      const detailIds = (task.lineages || []).filter((l) => l.status == 2 && l.generatedImage).map((l) => l.aiGenerateImageTaskDetailId);
      if (!detailIds || detailIds.length === 0) {
        this.$Message.warning("当前任务没有可下载的生成图");
        return;
      }
      this.downPicLoading = true;
      this.axios
        .post(
          api.aiGenerateImageTaskDownload + "?aiGenerateImageTaskId=" + aiGenerateImageTaskId,
          {},
          {
            responseType: "blob",
            isCache: false,
          },
        )
        .then((res) => {
          // 判断是否是blob
          if (res.resData instanceof Blob) {
            this.$common.downloadFile(res.resData, { name: res.filename });
            return;
          }
          this.$Message.error("下载失败");
        })
        .catch(() => {
          this.$Message.error("下载失败!");
        })
        .finally(() => {
          this.downPicLoading = false;
        });
    },
    /**
     * 删除单张 AI 生成图（二次确认）
     * @param {Object} task 主任务
     * @param {Object} lineage 当前子任务
     */
    deleteAiImage(task, lineage) {
      const { aiGenerateImageTaskId, aiGenerateImageTaskDetailId } = lineage || {};
      if (!aiGenerateImageTaskId || !aiGenerateImageTaskDetailId) {
        this.$Message.error("删除失败：缺少任务ID");
        return;
      }
      if (this.deleteLoadingMap[aiGenerateImageTaskDetailId]) {
        this.$Message.warning("删除中，请稍后");
        return;
      }
      this.$Modal.confirm({
        title: "删除确认",
        content: "确认删除该AI图片吗？",
        okText: "确认删除",
        cancelText: "取消",
        onOk: () => {
          this.$set(this.deleteLoadingMap, aiGenerateImageTaskDetailId, true);
          return new Promise((resolve) => {
            let query = "?aiGenerateImageTaskId=" + aiGenerateImageTaskId + "&aiGenerateImageTaskDetailId=" + aiGenerateImageTaskDetailId;
            this.axios
              .post(api.aiGenerateImageDetailDelete + query)
              .then((res) => {
                if (res && res.data && res.data.code == 0) {
                  this.$Message.success("删除成功");
                  this.loadTaskList();
                } else {
                  this.$Message.error((res && res.data && res.data.message) || "删除失败");
                }
              })
              .catch(() => {
                this.$Message.error("删除失败");
              })
              .finally(() => {
                this.$set(this.deleteLoadingMap, aiGenerateImageTaskDetailId, false);
                resolve();
              });
          });
        },
        onCancel: () => {},
      });
    },
    /**
     * 打开重新生成弹框
     * @param {Object} task 主任务
     * @param {Object} lineage 当前子任务
     */
    openRegenerateModal(task, lineage) {
      this.regenerateTask = task;
      this.regenerateLineage = lineage;
      this.regenerateModalVisible = true;
    },
    /** 重新生成弹框成功回调 */
    onRegenerateSuccess() {
      this.regenerateModalVisible = false;
      this.regenerateTask = null;
      this.regenerateLineage = null;
      this.loadTaskList();
    },
  },
};
</script>

<style scoped lang="less">
.ai-task-table-wrap {
  height: calc(100vh - 312px);
  min-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #e8eaec;
  border-radius: 4px;

  /* 隐藏滚动条宽度，保留滚动能力 */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE 10+ / 旧 Edge */

  &::-webkit-scrollbar {
    /* Chrome / Safari / 新 Edge */
    width: 0;
    height: 0;
    background: transparent;
  }
}

.ai-task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #e8eaec;
    text-align: left;
    color: #515a6e;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8f8f9;
    font-weight: 600;
    color: #17233d;
    white-space: nowrap;
    border-top: none;
    /* sticky 元素在滑动时上下边框会消失，用 box-shadow 模拟下边框 */
    box-shadow: inset 0 -1px 0 #e8eaec;
  }

  tbody .ai-task-row {
    &:hover td {
      background: #f0faff;
    }
  }
}

.ai-task-green {
  color: #19be6b;
  font-weight: 600;
}

.ai-task-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  background-color: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);

  &.is-created {
    color: #e7b04a;
    background-color: rgba(231, 176, 74, 0.1);
    border: 1px solid rgba(231, 176, 74, 0.1);
  }

  &.is-done {
    color: #19be6b;
    background-color: rgba(25, 190, 107, 0.1);
    border: 1px solid rgba(25, 190, 107, 0.1);
  }

  &.is-running {
    color: #2d8cf0;
    background-color: rgba(45, 140, 240, 0.1);
    border: 1px solid rgba(45, 140, 240, 0.1);
  }

  &.is-failed {
    color: #ed4014;
    background-color: rgba(237, 64, 20, 0.1);
    border: rgba(237, 64, 20, 0.1);
  }
}

/* 展开行滑动动画 */
.ai-task-expand-row td {
  padding: 0 !important;
  border-top: none !important;
}

.ai-task-expand-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.35s ease-in-out;

  &.is-expanded {
    max-height: 100%;
    transition: max-height 0.5s ease-in-out;
  }
}

.ai-task-expand-inner {
  padding: 10px 14px;
  background: #fafbfc;
  border-top: 1px solid #e8eaec;
}

.ai-task-detail-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #17233d;
  margin-bottom: 10px;

  .ai-task-detail-title {
    display: flex;
    align-items: center;

    i {
      margin-right: 6px;
      color: #2d8cf0;
    }
  }
}

/* 图片操作按钮组（重新生成 / 删除）- 常驻显示在标签下方 */
.ai-task-img-action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding-top: 4px;
}

.ai-task-action-link {
  font-size: 12px;
  color: #2d8cf0;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #57a3f3;
  }

  &.is-loading {
    color: #c5c8ce;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.ai-task-action-danger {
  color: #ed4014 !important;

  &:hover {
    color: #f16643 !important;
  }
}

.ai-task-action-divider {
  color: #dcdee2;
  font-size: 12px;
}

.ai-task-detail-params {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  margin-bottom: 18px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e8eaec;

  .ai-task-param-item {
    min-width: 180px;
    max-width: 100%;

    .ai-task-param-label {
      color: #808695;
      font-size: 12px;
      margin-bottom: 4px;
    }

    .ai-task-param-value {
      color: #17233d;
      font-size: 13px;
    }

    &.ai-task-param-full {
      flex-basis: 100%;
    }
  }

  .ai-task-prompt-text {
    word-break: break-all;
    line-height: 1.5;
  }
}

.ai-task-sync-tag {
  display: inline-block;
  padding: 2px 8px;
  margin-right: 6px;
  border: 1px solid #19be6b;
  color: #19be6b;
  border-radius: 4px;
  font-size: 12px;
}

.ai-task-lineage-header {
  font-size: 14px;
  font-weight: 600;
  color: #17233d;
  margin-bottom: 12px;

  i {
    margin-right: 6px;
  }
}

.ai-task-lineage-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ai-task-lineage-record {
  padding: 14px;
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.ai-task-lineage-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.ai-task-lineage-record-header {
  display: flex;
  align-items: center;
  flex-basis: 100%;
  gap: 8px;
  color: #808695;
  font-size: 12px;
  margin-bottom: 6px;
}

.ai-task-lineage-mode-tag {
  padding: 2px 8px;
  border: 1px solid #ffd591;
  border-radius: 3px;
  background: #fff7e6;
  color: #fa8c16;
  font-weight: 600;
}

.ai-task-lineage-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ai-task-lineage-img-item {
  text-align: center;
  cursor: pointer;

  img {
    width: 90px;
    height: 90px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dcdee2;
    transition: all 0.2s;

    &:hover {
      border-color: #2d8cf0;
      box-shadow: 0 0 6px rgba(45, 140, 240, 0.4);
    }
  }

  .ai-task-lineage-img-label {
    margin-top: 4px;
    font-size: 11px;
    color: #808695;
  }

  &.is-generated {
    position: relative;

    .ai-task-lineage-img-label {
      color: #19be6b;
      font-weight: 600;
    }
  }

  /* 无图片路径时的占位样式（尺寸与图片一致，提示“AI生成中”） */
  &.ai-task-lineage-img-placeholder {
    cursor: default;

    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 90px;
    height: 90px;
    border-radius: 4px;
    border: 1px dashed #c0c4cc;
    background: #fafbfc;

    .ai-task-placeholder-spin {
      font-size: 22px;
      color: #2d8cf0;
      animation: ai-task-spin 1s linear infinite;
    }

    .ai-task-lineage-img-label {
      color: #2d8cf0;
    }
  }

  /* 失败状态占位（上下两行：失败文字 + 分隔线 + 重新生成按钮） */
  &.ai-task-lineage-img-failed {
    cursor: default;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 90px;
    height: 90px;
    border-radius: 4px;
    border: 1px dashed #ed4014;
    background: rgba(237, 64, 20, 0.05);
    padding: 4px 0;
    position: relative;

    .ai-task-failed-text {
      color: #ed4014;
      font-weight: 600;
      font-size: 14px;
      line-height: 1;
    }

    /* 重新生成按钮：红底白字 12px */
    .ai-task-failed-retry-btn {
      // display: inline-block;
      padding: 2px;
      background: #ed4014;
      color: #fff;
      font-size: 12px;
      line-height: 1.4;
      // border-radius: 3px;
      cursor: pointer;
      user-select: none;
      transition: background 0.2s;
      position: absolute;
      bottom: -1px;
      left: -1px;
      right: -1px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;

      &:hover {
        background: #d93713;
      }

      &:active {
        background: #c4300f;
      }

      /* loading 状态：禁止点击 + 半透明 + 更换光标 */
      &.is-loading {
        background: #f5876c;
        cursor: not-allowed;
        pointer-events: none;
        /* 防止多次点击 */
      }

      /* loading 旋转图标 */
      .ai-task-retry-spin {
        font-size: 12px;
        animation: ai-task-spin 1s linear infinite;
      }
    }
  }
}

@keyframes ai-task-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.ai-task-lineage-prompt-section {
  display: flex;
  align-items: center;
  gap: 8px;
  /* flex: 1 1 33%：可伸缩，但不低于容器宽度的 1/3。
     当参考图+生成图展开后据压提示词区 < 1/3 时，
     flex-wrap: wrap 会自动将本区块整体换行到下一行 */
  flex: 1 1 33%;
  min-width: 33%;

  .ai-task-lineage-plus {
    font-size: 24px;
    color: #999;
    line-height: 1;
    // margin-top: 30px;
  }

  .ai-task-lineage-prompt-content {
    flex: 1;
    min-width: 0;
    border: 1px solid #e8eaec;
    border-radius: 4px;
    padding: 8px 10px;
    background-color: #fafbfc;

    .ai-task-lineage-prompt-title {
      font-size: 12px;
      color: #50535c;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .ai-task-lineage-prompt-text {
      font-size: 12px;
      color: #515a6e;
      word-break: break-all;
      // white-space: pre-wrap;
      line-height: 1.5;
    }

    /* 失败原因 + 重试按钮行（flex 布局，失败原因可多行，重试按钮距失败原因 50px） */
    .ai-task-lineage-error-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 50px;
      margin-top: 6px;
    }

    .ai-task-error-msg {
      flex: 1;
      min-width: 0;
      font-size: 10px;
      /* 比提示词（12px）小 2px */
      color: #ed4014;
      // border: 1px solid #ed4014;
      padding: 0px 10px 0 0;
      border-radius: 3px;
      word-break: break-all;
      line-height: 1.5;
    }
  }
}

.ai-task-lineage-arrow {
  font-size: 28px;
  color: #999;
  // margin-top: 30px;
  flex-shrink: 0;
}
</style>
